import type {
  ReportGridColumnSettings,
  ReportGridSettingsEntry,
} from '../../../models/reporting/report-grid-settings.model';
import {
  COMPARISON_DEFAULT_FILTER_OPERATION,
  COMPARISON_DEFAULT_TOP_COUNT,
  COMPARISON_SECONDARY_SORT,
  COMPARISON_SORT_CAPTIONS,
  getComparisonColumnFilterModes,
  type ComparisonColumnDefinition,
  type ComparisonTableDefinition,
} from './comparison-table-catalog';

/**
 * A viewer's arrangement of the Comparison report, read without the page.
 *
 * The page stores an arrangement as a preset does: the columns hidden page-wide, and per grid only
 * what the viewer changed from how the grid ships. Reading one back therefore means replaying it
 * over the catalog's defaults exactly the way DevExtreme replays it over the grid's — which column
 * wins a sort, which filters apply, what order the columns come in — or the printed table and the
 * one on screen part ways without anybody noticing.
 */

/** The part of a preset that says how the tables look; the period and the filter travel apart. */
export interface ComparisonArrangement {
  hiddenColumns?: string[];
  grids?: ReportGridSettingsEntry[];
  mgaTopCount?: number;
  agentTopCount?: number;
}

export interface ComparisonSortDescriptor {
  selector: string;
  desc: boolean;
}

export interface ResolvedComparisonTable {
  table: ComparisonTableDefinition;
  /** The columns on screen, in the order they are on screen. The name always leads. */
  columns: ComparisonColumnDefinition[];
  /** The order the rows are in; null for the overall table, which cannot be reordered. */
  sort: ComparisonSortDescriptor | null;
  /** The table's filter, as the grid sends it; null when nothing narrows it. */
  filter: unknown[] | null;
  /** How many rows the table holds; null when it holds every row it is given. */
  rowCount: number | null;
}

/**
 * The most rows one printed table may ask for.
 *
 * A mover table grows a page at a time for as long as the viewer keeps pressing, and every row
 * pressed in is a row printed. Past this it has stopped being a ranking anyone reads on paper.
 */
export const COMPARISON_MAX_PRINTED_ROWS = 1000;

/** The filter row operations a grid can put in a stored filter, and nothing else. */
const FILTER_ROW_OPERATIONS = new Set([
  '=',
  '<>',
  '<',
  '<=',
  '>',
  '>=',
  'between',
  'contains',
  'notcontains',
  'startswith',
  'endswith',
]);

function findEntry(arrangement: ComparisonArrangement, gridId: string): ReportGridSettingsEntry | undefined {
  return arrangement.grids?.find(entry => entry?.id === gridId);
}

function hasOwn<T extends object>(value: T | undefined, key: keyof T): boolean {
  return !!value && Object.prototype.hasOwnProperty.call(value, key);
}

/**
 * The columns a table shows, in order.
 *
 * A column hidden page-wide is off unless this grid's own picker turned it back on, which is
 * the order the page applies them in. Order comes from the stored visible indexes, which
 * DevExtreme records for every column a drag moved; the rest keep the place they ship in.
 */
function resolveColumns(
  table: ComparisonTableDefinition,
  entry: ReportGridSettingsEntry | undefined,
  hiddenColumns: ReadonlySet<string>,
): ComparisonColumnDefinition[] {
  return table.columns
    .map((column, index) => {
      const changed = entry?.columns?.[column.field];
      const isName = column.field === 'entityName';
      const visible = isName
        ? true
        : typeof changed?.visible === 'boolean'
          ? changed.visible
          : !hiddenColumns.has(column.field);
      const order = typeof changed?.visibleIndex === 'number' ? changed.visibleIndex : index;

      return { column, visible, order, index, isName };
    })
    .filter(candidate => candidate.visible)
    .sort((left, right) => {
      // The name is fixed to the left on every grid that can be reordered.
      if (left.isName !== right.isName) {
        return left.isName ? -1 : 1;
      }

      return left.order - right.order || left.index - right.index;
    })
    .map(candidate => candidate.column);
}

/**
 * The order the rows are in.
 *
 * A stored sort order of null is a sort the viewer took off, which is not the same as one they
 * never touched — so presence of the key is what is asked, not its value. Of the columns sorted,
 * the first by sort index wins; a mover table only orders on its figures, as the page does.
 */
function resolveSort(
  table: ComparisonTableDefinition,
  entry: ReportGridSettingsEntry | undefined,
): ComparisonSortDescriptor | null {
  if (!table.defaultSort) {
    return null;
  }

  const sorted = table.columns
    .map((column, index) => {
      const changed = entry?.columns?.[column.field];
      const isDefault = table.defaultSort?.field === column.field;
      const defaultOrder = isDefault ? (table.defaultSort?.desc ? 'desc' : 'asc') : null;
      const sortOrder = hasOwn(changed, 'sortOrder') ? changed?.sortOrder : defaultOrder;
      const sortIndex = hasOwn(changed, 'sortIndex') ? changed?.sortIndex : isDefault ? 0 : null;

      return { field: column.field, sortOrder, sortIndex, index };
    })
    .filter(candidate => candidate.sortOrder === 'asc' || candidate.sortOrder === 'desc')
    .filter(candidate => table.kind !== 'mover' || !!COMPARISON_SORT_CAPTIONS[candidate.field])
    .sort(
      (left, right) =>
        (left.sortIndex ?? Number.MAX_SAFE_INTEGER) - (right.sortIndex ?? Number.MAX_SAFE_INTEGER) ||
        left.index - right.index,
    );

  const primary = sorted[0];

  // A grid with its sort cleared keeps the order it already had, as the page's table does.
  return primary
    ? { selector: primary.field, desc: primary.sortOrder === 'desc' }
    : { selector: table.defaultSort.field, desc: table.defaultSort.desc };
}

function isFilterValueSet(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

/** One header filter value as DevExtreme reads it: a stored expression as it is, a value as "=". */
function toHeaderFilterValueCondition(column: ComparisonColumnDefinition, value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }

  // Blank names are stored as null, and a text column's null also matches the empty string.
  if (value === null && column.kind === 'text') {
    return [[column.field, '=', null], 'or', [column.field, '=', '']];
  }

  return [column.field, '=', value];
}

/** Joins conditions the way DevExtreme does: one stands alone, several share a joiner. */
function combine(conditions: unknown[][], joiner: 'and' | 'or'): unknown[] | null {
  if (!conditions.length) {
    return null;
  }

  if (conditions.length === 1) {
    return conditions[0];
  }

  return conditions.flatMap((condition, index) => (index === 0 ? [condition] : [joiner, condition]));
}

/** A filter row's condition, or null when the stored value says nothing a grid would apply. */
function toFilterRowCondition(
  column: ComparisonColumnDefinition,
  changed: ReportGridColumnSettings | undefined,
): unknown[] | null {
  const value = changed?.filterValue;
  if (!isFilterValueSet(value)) {
    return null;
  }

  const storedOperation = hasOwn(changed, 'selectedFilterOperation') ? changed?.selectedFilterOperation : undefined;
  const operation = storedOperation ?? (column.kind === 'text' ? 'contains' : COMPARISON_DEFAULT_FILTER_OPERATION);

  if (!FILTER_ROW_OPERATIONS.has(operation)) {
    return null;
  }

  if (operation === 'between') {
    if (!Array.isArray(value) || !isFilterValueSet(value[0]) || !isFilterValueSet(value[1])) {
      return null;
    }

    return [[column.field, '>=', value[0]], 'and', [column.field, '<=', value[1]]];
  }

  return [column.field, operation, value];
}

/** A header filter's condition: any of its values, or none of them when it excludes. */
function toHeaderFilterCondition(
  column: ComparisonColumnDefinition,
  changed: ReportGridColumnSettings | undefined,
): unknown[] | null {
  const values = changed?.filterValues;
  if (!Array.isArray(values) || !values.length) {
    return null;
  }

  const anyOf = combine(
    values.map(value => toHeaderFilterValueCondition(column, value)),
    'or',
  );

  if (!anyOf) {
    return null;
  }

  return changed?.filterType === 'exclude' ? ['!', anyOf] : anyOf;
}

/**
 * The table's filter, built the way the grid builds the one it sends.
 *
 * Only columns on screen count: DevExtreme drops a hidden column's filter along with the column,
 * so a filter left on a column the viewer then hid narrows nothing on the page and must narrow
 * nothing here. Each column contributes its filter row and its header filter where the column
 * offers them, and every contribution must hold.
 */
function resolveFilter(
  table: ComparisonTableDefinition,
  entry: ReportGridSettingsEntry | undefined,
  columns: readonly ComparisonColumnDefinition[],
): unknown[] | null {
  const conditions = columns.flatMap(column => {
    const changed = entry?.columns?.[column.field];
    const modes = getComparisonColumnFilterModes(table, column);

    return [
      modes.filterRow ? toFilterRowCondition(column, changed) : null,
      modes.headerFilter ? toHeaderFilterCondition(column, changed) : null,
    ].filter((condition): condition is unknown[] => condition !== null);
  });

  return combine(conditions, 'and');
}

/**
 * How many rows a mover table is showing: however far it had been grown, else the size it
 * opens at, else the page's top count for its level.
 *
 * A table that loaded and found nothing stored 0, and prints nothing — which is what it showed.
 */
function resolveRowCount(
  table: ComparisonTableDefinition,
  entry: ReportGridSettingsEntry | undefined,
  arrangement: ComparisonArrangement,
): number | null {
  if (table.kind !== 'mover') {
    return null;
  }

  const topCount = table.level === 'agent' ? arrangement.agentTopCount : arrangement.mgaTopCount;
  const stored = entry?.table;
  const count =
    typeof stored?.loadedCount === 'number'
      ? stored.loadedCount
      : typeof stored?.pageSize === 'number'
        ? stored.pageSize
        : (topCount ?? COMPARISON_DEFAULT_TOP_COUNT);

  return Math.min(Math.max(Math.floor(count), 0), COMPARISON_MAX_PRINTED_ROWS);
}

export function resolveComparisonTable(
  table: ComparisonTableDefinition,
  arrangement: ComparisonArrangement,
): ResolvedComparisonTable {
  const entry = findEntry(arrangement, table.gridId);
  const columns = resolveColumns(table, entry, new Set(arrangement.hiddenColumns ?? []));

  return {
    table,
    columns,
    sort: resolveSort(table, entry),
    filter: table.kind === 'overall' ? null : resolveFilter(table, entry, columns),
    rowCount: resolveRowCount(table, entry, arrangement),
  };
}

/**
 * The load options a resolved table's rows are asked for with — the ones the page's own table
 * sends: its order, then the name to settle ties, its filter, and its size.
 */
export function buildComparisonTableLoadOptions(resolved: ResolvedComparisonTable): Record<string, unknown> {
  return {
    ...(resolved.sort ? { sort: [resolved.sort, COMPARISON_SECONDARY_SORT] } : {}),
    ...(resolved.filter ? { filter: resolved.filter } : {}),
    skip: 0,
    ...(resolved.rowCount !== null ? { take: resolved.rowCount } : {}),
  };
}
